import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Sprout, Sun, Cog, Droplets, PackageCheck } from "lucide-react";
import { Overline, Reveal, MaskLine } from "./shared";
import { useLang } from "@/i18n";

const icons = [Sprout, Sun, Cog, Droplets, PackageCheck];

export default function Process() {
  const { t } = useLang();
  const steps = t("process.steps");
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <section id="process" className="mx-auto max-w-7xl px-6 py-24 lg:px-12 lg:py-32" data-testid="process-section">
      <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Overline>{t("process.overline")}</Overline>
          <h2 className="mt-4 font-serif text-4xl font-medium leading-tight tracking-tight sm:text-5xl">
            <MaskLine>{t("process.title1")}</MaskLine>
            <MaskLine delay={0.12}>
              <span className="italic text-terracotta">{t("process.title2")}</span>
            </MaskLine>
          </h2>
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-sm text-base font-light leading-relaxed text-moss">
              {t("process.intro")}
            </p>
          </Reveal>
          <Reveal delay={0.3} className="mt-10">
            <div ref={imgRef} className="arch-frame relative aspect-[3/4] w-full max-w-xs overflow-hidden border border-ink/10">
              <motion.img
                src="/assets/farm/process-press.jpeg"
                alt="The wood press extracting oil at the Sira Shakti unit"
                style={{ y, scale: 1.2 }}
                className="h-full w-full object-cover"
              />
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <ol className="mt-2 space-y-0">
            {steps.map((s, i) => {
              const Icon = icons[i];
              return (
                <Reveal key={s.title} delay={i * 0.08}>
                  <li className="flex gap-6 border-b border-ink/10 py-8 first:pt-0" data-testid={`process-step-${i + 1}`}>
                    <span className="font-serif text-2xl font-light text-ochre">{String(i + 1).padStart(2, "0")}</span>
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-olive/30 text-olive">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-serif text-xl font-semibold tracking-tight sm:text-2xl">{s.title}</h3>
                      <p className="mt-2 max-w-md text-sm font-light leading-relaxed text-moss">{s.body}</p>
                    </div>
                  </li>
                </Reveal>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
  );
}
